/*
 * Copyright (c) 2023 Helvia Technologies PC <contact@helvia.io>
 */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";

// components imports
import { Col, Row, Tabs, Modal, Result, Flex } from "antd";
import type { TabsProps } from "antd";
import { LineChartOutlined, SettingOutlined } from "@ant-design/icons";
import { Container } from "@/components/UI/Container";
import { ProjectAnalytics } from "@/components/ProjectsComponents/Analytics/ProjectAnalytics";
import { ServiceTypeSettings } from "@/components/ProjectsComponents/Settings/ServiceTypeSettings";
import { DataLoadingSkeleton } from "@/components/UI/DataLoadingSkeleton";
import { ScreenTopBar } from "@/components/ScreenTopBar";
import { ManageProjectModal } from "@/components/ProjectsComponents/ManageProjectModal";
import { DeleteProject } from "@/components/ProjectsComponents/DeleteProject";

// misc imports
import { useGetProject } from "@/api/projects";
import { SERVICE_TYPE_SIMPLIFIED } from "@/interfaces/Service.interface";
import { ICheckoutStatus } from "@/interfaces/Checkout.interface";
import replaceUrlParams from "@/utils/string/replaceUrlParams";
import { AUTH_TOKEN_NOT_VALID, NAV_LOGIN, NAV_PROJECTS } from "@/constants";
import { queryClient } from "@/utils/queryClient";
import { useUserInfo } from "@/api/user";
import { tokens as Tokens } from "@/utils/tokens";
import { ITokens } from "@/interfaces/User.interface";

/**
 * Component to handle Project Single view
 * @returns JSX.Element
 */
export const ProjectSingleView = (): JSX.Element => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const tokensInstance = Tokens;
  const { pathname, search } = location;
  const { workspaceId, projectId } = useParams();
  const params = {
    workspaceId: workspaceId as string,
    projectId: projectId as string,
  };

  const userAuth: ITokens | undefined = queryClient.getQueryData(["user-auth"]);
  const { access: accessToken, refresh: refreshToken } = userAuth || {};
  const { state } = location;
  const { status }: { status: ICheckoutStatus } = state ?? {};
  const { data: userInfoData } = useUserInfo(tokensInstance.getTokenSub(accessToken) as string);

  if (status === "complete" && userInfoData?.uuid && !userInfoData?.currency) {
    queryClient.invalidateQueries({
      queryKey: ["user-info", userInfoData.uuid],
    });
  }
  
  const searchParams = new URLSearchParams(location.search);
  const currentTab = searchParams.get("tab") || "";

  // Fetch project data
  const {
    data: projectData,
    isLoading: isLoadingProject,
    error: projectDataError,
  } = useGetProject({
    ...params,
  });

  // Tabs List - Settings, Api Keys, Analytics
  const [items, setItems] = useState<TabsProps["items"] & { forceRender?: boolean }>([]);
  const next = encodeURIComponent(pathname + search);
  // USE EFFECTS
  useEffect(() => {
    if (!projectDataError) {
      return;
    }
    if (!refreshToken || tokensInstance.isTokenExpired(refreshToken)) {
      queryClient.clear();
      navigate(`${NAV_LOGIN}?next=${next}`, { state: { from: location } });
      return;
    }
    if ((projectDataError as any).code !== AUTH_TOKEN_NOT_VALID) {
      navigate(replaceUrlParams(NAV_PROJECTS, { workspaceId: workspaceId as string }), { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectDataError]);

  useEffect(() => {
    if (!projectData?.services) {
      return;
    }
    // Settings tab
    const _items = [
      {
        key: "services",
        label: t("projects.services"),
        children: <ServiceTypeSettings />,
        icon: <SettingOutlined />,
        forceRender: true,
      },
    ];
    // If Analytics exists as a service to the project then add another item to the tabs
    const hasAnalyticsService = projectData?.services.find((service) => service.service_type === SERVICE_TYPE_SIMPLIFIED.ANALYTICS);
    if (hasAnalyticsService) {
      _items.push({
        key: "analytics",
        label: t("projects.analytics"),
        children: <ProjectAnalytics />,
        icon: <LineChartOutlined />,
        forceRender: true,
      });
    }
    // Map all available keys to set the URL params
    const tabKeys = _items.map((i) => i.key);
    if (!tabKeys.includes(currentTab)) {
      if (!(searchParams.get("tab") === tabKeys[0])) {
        setTimeout(() => {
          changeTab(tabKeys[0]);
        });
      }
    }
    // Update the state
    setItems(_items);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectData?.services]);

  const changeTab = (key: string) => {
    const nextUrlSearchParams = new URLSearchParams();
    nextUrlSearchParams.set("tab", key);
    navigate(
      {
        pathname: location.pathname,
        search: nextUrlSearchParams.toString(),
      },
      { replace: true, state }
    );
  };

  if (!projectData && isLoadingProject) {
    return <DataLoadingSkeleton />;
  }

  return (
    <Container>
      {/* Back Button to project list */}
      <ScreenTopBar />

      {/* Project Main content */}
      {projectData?.services.length ? (
        <Row className="w-100" justify={"center"}>
          <Col xs={24}>
            {/* TABS to handle single project navigation */}
            <Tabs
              activeKey={currentTab}
              onChange={changeTab}
              items={items}
              indicator={{ size: 0 }}
              tabBarExtraContent={
                <Flex align="center">
                  <ManageProjectModal projectData={projectData}/>
                  <DeleteProject />
                </Flex>
              }
            />
            {/* ./TABS to handle single project navigation */}
          </Col>
        </Row>
      ) : null}
      {/* ./ Project Main content */}
      {/* Confirmation Modal when upgrading plans */}
      <Modal
        open={status === "complete"}
        centered
        onCancel={() =>
          navigate(
            {
              pathname: location.pathname,
              search: location.search.toString(),
            },
            { replace: true, state: null }
          )
        }
        footer={[]}
      >
        <Result status="success" title={t("subscription_plans.upgrade_success")} />
      </Modal>
      {/* /. Confirmation Modal when upgrading plans */}
    </Container>
  );
};
